import format from "date-fns/format";
import isValid from "date-fns/isValid";

// export const formatDate = (dateString) => {
//   if (!dateString) return "";
//   const date = new Date(dateString);
//   return date.toLocaleDateString("en-US", {
//     year: "numeric",
//     month: "long",
//     day: "numeric",
//   });
// };
// export const formatDate = (dateString) => {
//   if (!dateString) return "";

//   return new Date(dateString).toLocaleDateString("en-US", {
//     year: "numeric",
//     month: "long",
//     day: "numeric",
//     timeZone: "UTC",
//   });
// };

/**
 * Format a DATE-ONLY value (no meaningful time-of-day) without ever shifting
 * the calendar date across timezones. Backend date-only fields arrive as
 * `YYYY-MM-DD` (or an ISO string whose date part is authoritative); rendering
 * them with a bare `new Date(x).toLocaleDateString()` shifts them back a day in
 * negative-offset zones (e.g. Eastern), which is the off-by-one bug this guards
 * against. For TIMESTAMPS (values with a meaningful time-of-day) use
 * `formatDateTime` instead — see the "Dates & times" convention in CLAUDE.md.
 *
 * @param {string|Date} value
 * @param {string} [returnValue=""]   fallback when the value is empty/invalid
 * @param {string} [pattern]          optional date-fns pattern (e.g. "MMM d, yyyy",
 *                                     "MMM yyyy", "M/d/yyyy"). When omitted, renders
 *                                     long-month "June 10, 2026". Either way the
 *                                     result is timezone-stable (UTC calendar date).
 * @returns {string}
 */
/**
 * The ONE rule for reading a date-only value: its calendar day is the literal
 * leading `YYYY-MM-DD` of the string — never a timezone conversion of it. A
 * `Date` instance contributes its UTC calendar day (that is what DateInput
 * writes: UTC midnight of the picked day). Every date-only reader (formatDate,
 * DateInput, day countdowns) goes through this so they can never disagree.
 *
 * @returns {{y:number,m:number,d:number}|null}  m is 1-based
 */
export const dateOnlyParts = (value) => {
  if (value == null || value === "") return null;
  const s =
    value instanceof Date
      ? Number.isNaN(value.getTime())
        ? ""
        : value.toISOString()
      : String(value);
  const datePart = s.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
  const [y, m, d] = datePart.split("-").map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
};

/** A LOCAL-midnight Date on the value's calendar day (for date pickers). */
export const dateOnlyToLocalDate = (value) => {
  const p = dateOnlyParts(value);
  return p ? new Date(p.y, p.m - 1, p.d) : null;
};

/**
 * Epoch ms at 12:00 UTC on the value's calendar day. Noon UTC lands on the
 * same calendar day in every zone from UTC-11 to UTC+11 (Eastern included),
 * so it can be fed to Eastern timestamp formatters and axis math safely.
 */
export const dateOnlyMs = (value) => {
  const p = dateOnlyParts(value);
  return p ? Date.UTC(p.y, p.m - 1, p.d, 12) : null;
};

/**
 * Whole calendar days from TODAY (the Eastern day — the desk's zone) to the
 * value's calendar day. Negative = in the past. null when unparseable.
 */
export const daysUntilDateOnly = (value) => {
  const p = dateOnlyParts(value);
  if (!p) return null;
  const [mo, da, yr] = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  })
    .format(new Date())
    .split("/")
    .map(Number);
  return Math.round(
    (Date.UTC(p.y, p.m - 1, p.d) - Date.UTC(yr, mo - 1, da)) / 86400000,
  );
};

/** "YYYY-MM-DD" key for comparing two date-only values; null when unparseable. */
export const dateOnlyKey = (value) => {
  const p = dateOnlyParts(value);
  return p
    ? `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`
    : null;
};

export const formatDate = (value, returnValue = "", pattern = null) => {
  try {
    const parts = dateOnlyParts(value);
    if (!parts) return returnValue;
    const { y, m, d } = parts;

    // Custom pattern: build a LOCAL date carrying the same calendar fields so
    // date-fns `format` renders exactly Y/M/D with no timezone shift.
    if (pattern) {
      const local = new Date(y, m - 1, d);
      if (!isValid(local)) return returnValue;
      return format(local, pattern);
    }

    const date = new Date(Date.UTC(y, m - 1, d));
    if (Number.isNaN(date.getTime())) return returnValue;

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return returnValue;
  }
};
